import {
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Switch,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import type { AutoTransactionRuleDto } from "../../../../api/types";
import {
  describeProblems,
  isUnreadable,
} from "../../../../logic/rule-problems";

export type AutoTransactionActionsProps = {
  rule: AutoTransactionRuleDto;
  onEdit: (rule: AutoTransactionRuleDto) => void;
  onDuplicate: (rule: AutoTransactionRuleDto) => void;
  /** What the rule's condition matches now, whether or not it has ever run. */
  onPreviewMatches: (rule: AutoTransactionRuleDto) => void;
  /** What the rule has actually been applied to. */
  onShowApplied: (rule: AutoTransactionRuleDto) => void;
  onDelete: (rule: AutoTransactionRuleDto) => void;
  onToggleEnabled: (rule: AutoTransactionRuleDto, next: boolean) => void;
  isTogglingEnabled?: boolean;
  size?: "small" | "medium";
};

export const AutoTransactionActions = ({
  rule,
  onEdit,
  onDuplicate,
  onPreviewMatches,
  onShowApplied,
  onDelete,
  onToggleEnabled,
  isTogglingEnabled = false,
  size = "medium",
}: AutoTransactionActionsProps) => {
  const spinnerSize = size === "small" ? 16 : 20;

  // Two different questions, and they used to share one button and one name.
  // "What does this match?" is answered from the condition, so it works on a
  // rule that has never run; "what has it touched?" is answered from the
  // recorded edits, so it is empty until the rule is applied. Reading the
  // second as the first makes a working rule look broken.
  const matchButtons = (
    <>
      <Tooltip title="Show transactions this rule matches">
        <IconButton
          size={size}
          aria-label={`show transactions matching ${rule.name}`}
          onClick={() => onPreviewMatches(rule)}
        >
          <ManageSearchIcon fontSize={size} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Show transactions this rule has been applied to">
        <IconButton
          size={size}
          aria-label={`show transactions ${rule.name} was applied to`}
          onClick={() => onShowApplied(rule)}
        >
          <ReceiptLongIcon fontSize={size} />
        </IconButton>
      </Tooltip>
    </>
  );

  const brokenChip = rule.problems.length > 0 && (
    // A rule with a problem does not run at all, whatever its enable
    // toggle says — so it gets a louder marker than the toggle.
    <Tooltip
      title={`This rule does not run: it ${describeProblems(rule.problems)}.`}
    >
      <Chip
        size="small"
        color="error"
        icon={<WarningAmberIcon fontSize="small" />}
        label="Broken"
      />
    </Tooltip>
  );

  const deleteButton = (
    <Tooltip title="Delete rule">
      <IconButton
        size={size}
        color="error"
        aria-label="delete"
        onClick={() => onDelete(rule)}
      >
        <DeleteIcon fontSize={size} />
      </IconButton>
    </Tooltip>
  );

  // A stored row the backend could not parse: what is listed is a stub, so
  // nothing that would PUT it back — the toggle, edit, duplicate — is offered;
  // it would write the stub's empty shape over the stored rule. Delete is the
  // one fix there is, so that stays.
  if (isUnreadable(rule.problems)) {
    return (
      <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
        {brokenChip}
        {matchButtons}
        {deleteButton}
      </Stack>
    );
  }

  // Server-generated virtual rules (invoice matchers) are read-only: no edit,
  // delete, duplicate, or enable toggle. You can still inspect what they match,
  // and apply them from a matching transaction.
  if (!rule.editable) {
    return (
      <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
        <Tooltip title="Generated from an invoice — read-only. Apply it from a matching transaction.">
          <Chip
            size="small"
            icon={<AutoAwesomeIcon fontSize="small" />}
            label="Invoice"
            variant="outlined"
          />
        </Tooltip>
        {matchButtons}
      </Stack>
    );
  }

  return (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
      {brokenChip}
      <Tooltip
        title={
          rule.enabled
            ? "Enabled — click to disable"
            : "Disabled — click to enable"
        }
      >
        <span>
          {isTogglingEnabled ? (
            <CircularProgress size={spinnerSize} sx={{ mx: 1.25 }} />
          ) : (
            <Switch
              size={size}
              checked={rule.enabled}
              onChange={(_, next) => onToggleEnabled(rule, next)}
              slotProps={{
                input: { "aria-label": `toggle ${rule.name} enabled` },
              }}
            />
          )}
        </span>
      </Tooltip>
      {matchButtons}
      <Tooltip title="Edit rule">
        <IconButton size={size} aria-label="edit" onClick={() => onEdit(rule)}>
          <EditIcon fontSize={size} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Duplicate rule">
        <IconButton
          size={size}
          aria-label="duplicate"
          onClick={() => onDuplicate(rule)}
        >
          <ContentCopyIcon fontSize={size} />
        </IconButton>
      </Tooltip>
      {deleteButton}
    </Stack>
  );
};
